import { Upload, X, CheckCircle, AlertCircle, FileUp, Database, Check, File, Clock } from 'lucide-react';
import translations from '../../../../translations/PurchaseInvoiceTranslation';

const PendingFilesList = ({
  files,
  onRemove,
  onUpload,
  isUploading,
  uploadProgress,
  uploadStatus,
  uploadStage,
  styles
}) => {
  if (files.length === 0) return null;

  const getUploadStageText = () => {
    switch (uploadStage) {
      case 'uploading':
        return translations.uploading || 'מעלה...';
      case 'extracting':
        return translations.extracting || 'מחלץ נתונים...';
      case 'processing':
        return translations.processing || 'מעבד...';
      case 'completed':
        return translations.completed || 'הושלם';
      default:
        return isUploading ? (translations.uploading || 'מעלה...') : '';
    }
  };

  const getUploadStageIcon = () => {
    switch (uploadStage) {
      case 'uploading':
        return <FileUp size={16} />;
      case 'extracting':
        return <Database size={16} />;
      case 'processing':
      case 'completed':
        return <Check size={16} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes dotBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {files.length > 0 && (
        <div style={{ 
          fontSize: '1rem', 
          fontWeight: '600', 
          color: '#334155', 
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <Clock size={18} style={{ color: '#3b82f6' }} />
          {translations.pendingFiles || 'קבצים ממתינים להעלאה'}
          <span style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '10px',
            marginRight: '4px'
          }}>
            {files.length}
          </span>
        </div>
      )}
      
      <div style={styles.fileList}>
        {files.map((file, index) => (
          <div 
            key={index} 
            style={{
              ...styles.fileItem,
              animation: 'fadeIn 0.4s ease-out',
              animationDelay: `${index * 0.1}s`,
              animationFillMode: 'backwards'
            }}
          >
            <span style={styles.fileName}>
              <File size={16} style={{ color: '#3b82f6' }} />
              {file.name}
            </span>
            <button
              onClick={() => onRemove(index)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fee2e2';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X size={16} style={{ color: '#ef4444' }} />
            </button>
          </div>
        ))}
      </div>

      <button
        style={{
          ...styles.uploadButton,
          ...(isUploading ? styles.uploadButtonDisabled : {}),
          transition: 'all 0.3s ease'
        }}
        onClick={onUpload}
        disabled={isUploading}
        onMouseEnter={(e) => {
          if (!isUploading) {
            e.currentTarget.style.backgroundColor = '#2563eb';
            e.currentTarget.style.transform = 'scale(1.02)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isUploading) {
            e.currentTarget.style.backgroundColor = '#3b82f6';
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
      >
        {isUploading ? getUploadStageText() : translations.uploadToServer}
        {!isUploading && <Upload size={16} />}
        {isUploading && getUploadStageIcon()}
      </button>

      {(isUploading || uploadStatus) && (
        <div style={{
          ...styles.progressContainer,
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${uploadProgress}%`
              }}
            />
          </div>

          {uploadStatus && (
            <div
              style={{
                ...styles.uploadStatus,
                ...(uploadStatus === 'success' ? styles.success : 
                    uploadStatus === 'requires_attention_success' ? styles.success :
                    uploadStatus?.type === 'PROCESSING' ? { 
                      color: '#3b82f6', 
                      backgroundColor: '#EFF6FF',
                      borderColor: '#3b82f6'
                    } :
                    uploadStatus?.type === 'requires_attention' ? styles.success :
                    uploadStatus?.type === 'warning' ? { color: '#EA580C' } : styles.error),
                animation: 'fadeIn 0.5s ease-out'
              }}
            >
              {uploadStatus === 'success' ? (
                <>
                  <CheckCircle size={16} />
                  {translations.uploadSuccess}
                </>
              ) : uploadStatus?.type === 'PROCESSING' ? (
                <>
                  <CheckCircle size={16} style={{ color: '#3b82f6' }} />
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#3b82f6' }}>✅ חשבונית הועלתה בהצלחה</div>
                    <div style={{ fontSize: '12px', marginTop: '4px', color: '#3b82f6' }}>
                      {uploadStatus.message || 'החשבונית נמצאת בתהליך עיבוד ברקע'}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8, color: '#3b82f6' }}>
                      הסטטוס יתעדכן אוטומטית בסיום הבדיקות
                    </div>
                  </div>
                </>
              ) : uploadStatus === 'requires_attention_success' ? (
                <>
                  <CheckCircle size={16} />
                  <div>
                    <div>✅ חשבונית הועלתה בהצלחה למערכת טיפול</div>
                    <div style={{ fontSize: '12px', marginTop: '4px', color: '#EA580C' }}>
                      נדרש תיקון נתונים לפני אישור סופי
                    </div>
                  </div>
                </>
              ) : uploadStatus?.type === 'requires_attention' ? (
                <>
                  <CheckCircle size={16} />
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#22c55e' }}>✅ חשבונית הועלתה בהצלחה למערכת טיפול</div>
                    <div style={{ fontSize: '12px', marginTop: '4px', whiteSpace: 'pre-line', color: '#EA580C' }}>
                      {uploadStatus.message}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                      החשבונית נשמרה וניתנת לעריכה בטבלה
                    </div>
                  </div>
                </>
              ) : uploadStatus?.type === 'warning' ? (
                <>
                  <AlertCircle size={16} />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>⚠️ חשבונית הועלתה עם בעיות שניתנות לתיקון</div>
                    <div style={{ fontSize: '12px', marginTop: '4px', whiteSpace: 'pre-line' }}>
                      {uploadStatus.message}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                      החשבונית נשמרה במערכת טיפול לעריכה ידנית
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle size={16} />
                  {typeof uploadStatus === 'string' ? uploadStatus : uploadStatus?.message || 'שגיאה לא ידועה'}
                </>
              )}
            </div>
          )}

          {/* נקודות קופצות בזמן העלאה */}
          {isUploading && (
            <div style={{ 
              display: 'flex', 
              gap: '6px', 
              justifyContent: 'center',
              marginTop: '12px'
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#3b82f6',
                    borderRadius: '50%',
                    animation: 'dotBounce 1.8s ease-in-out infinite',
                    animationDelay: `${i * 0.2}s`
                  }}
                ></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingFilesList;
